import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { whatsappApi, normalizeWhatsAppPhone, prefillDocumentWhatsAppPhone, extractWhatsAppErrors } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const WhatsAppSendContext = createContext(null);

export function WhatsAppSendProvider({ children }) {
  const [draft, setDraft] = useState(null);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const lock = useRef(false);
  const { user } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => { setDraft(null); setConsent(false); }, [user?.id, pathname]);
  const requestWhatsAppSend = useCallback(({ phone = "", message, title = "WhatsApp message", companyId = localStorage.getItem("vg_company") }) => {
    if (lock.current) return;
    if (companyId !== localStorage.getItem("vg_company")) return toast.error("The active company changed. Open the document again before sending.");
    setConsent(false);
    setError("");
    setDraft({ phone: prefillDocumentWhatsAppPhone(phone), message: String(message || ""), title, companyId });
  }, []);
  const close = () => { if (!lock.current) setDraft(null); };
  const submit = async (event) => {
    event.preventDefault();
    if (lock.current || !draft || !consent) return;
    const phone = normalizeWhatsAppPhone(draft.phone);
    if (!phone) return setError("Enter an international phone number including its country code.");
    if (!draft.message.trim() || draft.message.length > 4096) return setError("Enter a message of 1–4096 characters.");
    if (draft.companyId !== localStorage.getItem("vg_company")) {
      setDraft(null);
      return toast.error("The active company changed. Open the document again before sending.");
    }
    lock.current = true;
    setSending(true);
    setError("");
    try {
      const { data: result } = await whatsappApi.send({ phone, message: draft.message, recipient_consent: consent });
      if (result?.ok !== true || result?.status !== "submitted") {
        setError("WhatsApp did not confirm submission. Delivery is uncertain; wait before retrying.");
        return;
      }
      toast.success("Message submitted to WhatsApp", { description: "Submission is confirmed; delivery is not yet confirmed." });
      setDraft(null);
    } catch (e) {
      setError(extractWhatsAppErrors(e).messages.join(" "));
    } finally {
      lock.current = false;
      setSending(false);
    }
  };
  return <WhatsAppSendContext.Provider value={requestWhatsAppSend}>
    {children}
    <Dialog open={Boolean(draft)} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Review &amp; send via WhatsApp</DialogTitle>
          <DialogDescription>{draft?.title}. Nothing is sent until you confirm below. Connect your company’s WhatsApp account in Settings first.</DialogDescription>
        </DialogHeader>
        {draft && <form onSubmit={submit} className="space-y-4">
          <label className="block space-y-1 text-sm">Recipient phone
            <Input value={draft.phone} disabled={sending} onChange={(e) => { setConsent(false); setDraft({ ...draft, phone: e.target.value }); }} placeholder="+91 followed by the mobile number" data-testid="document-whatsapp-phone" />
            <span className="block text-xs text-muted-foreground">Include the country code. Local Indian mobile numbers are prefixed with +91; check or edit the recipient before sending.</span>
          </label>
          <label className="block space-y-1 text-sm">Message
            <textarea className="w-full min-h-48 rounded-md border p-3 text-sm" value={draft.message} disabled={sending} onChange={(e) => { setConsent(false); setDraft({ ...draft, message: e.target.value }); }} data-testid="document-whatsapp-message" />
            <span className="block text-xs text-muted-foreground">{draft.message.length}/4096 characters</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={consent} disabled={sending} onChange={(e) => setConsent(e.target.checked)} data-testid="document-whatsapp-consent" />
            <span>I confirm that this recipient has opted in to receive this WhatsApp message.</span>
          </label>
          {error && <p role="alert" className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={sending} onClick={close}>Cancel</Button>
            <Button type="submit" disabled={sending || !consent || !normalizeWhatsAppPhone(draft.phone) || !draft.message.trim() || draft.message.length > 4096} data-testid="document-whatsapp-send">{sending ? "Submitting…" : "Confirm & send"}</Button>
          </DialogFooter>
        </form>}
      </DialogContent>
    </Dialog>
  </WhatsAppSendContext.Provider>;
}

export const useWhatsAppSender = () => useContext(WhatsAppSendContext);
